/**
 * @file ReceiptNotices.tsx
 * @description Зона полос карточки приёмки (phase19.23 — «без адреса»;
 * phase19.22 — причина приёмки, блок «Этикетки», устаревшие этикетки, срок).
 *
 * Черновик: «Причина приёмки» (+ парное списание) и предупреждение о строках
 * без срока. Проведённая: блок «Этикетки», полоса про устаревшие этикетки
 * (для пищевого — переклейка обязательна), напоминание о партиях без адреса.
 * Порядок полос — от того, что мешает работать сейчас, к справочному.
 */

import { ReceiptReasonFields } from "./ReceiptReasonFields";
import { LABEL_TEXT, staleNotice } from "./labels/label-texts";
import { ReceiptLabelsBlock } from "./labels/ReceiptLabelsBlock";
import { focusReceiptReasonFields, type ReceiptCommitBlock } from "./receipt-commit-block";
import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import type { ReceiptLabelStatusDto } from "../../lib/api-labels";
import type { GoodsReceiptDto, GoodsReceiptReason } from "../../lib/api-wms";
import { missingExpiryTitle } from "../../lib/receipt-expiry";

interface Props {
  receipt: GoodsReceiptDto;
  labels: ReceiptLabelStatusDto | null;
  /** Право печатать этикетки (просмотр склада). */
  canPrint: boolean;
  onPrint: () => void;
  onOpenHistory: () => void;
  onReasonChange: (reason: GoodsReceiptReason, parentWriteoffId: string | null) => void;
  /** Блоки проведения (решение 2026-09-26): красная полоса со ссылкой к полю. */
  blocks?: ReceiptCommitBlock[];
}

export function ReceiptNotices({
  receipt,
  labels,
  canPrint,
  onPrint,
  onOpenHistory,
  onReasonChange,
  blocks = [],
}: Props) {
  const isDraft = receipt.status === "DRAFT";
  const items = receipt.items ?? [];
  const missing = receipt.missingExpiryItems?.length ?? 0;
  const lotsTotal = new Set(items.map((it) => it.lotId).filter(Boolean)).size;
  const unplaced = receipt.unplacedLotsCount ?? 0;

  if (isDraft) {
    return (
      <>
        {/* Старый сервер причину не отдаёт — поле не показываем, чтобы не врать. */}
        {receipt.reason ? (
          <ReceiptReasonFields
            warehouseId={receipt.warehouseId}
            reason={receipt.reason}
            parentWriteoffId={receipt.parentWriteoffId ?? null}
            onChange={onReasonChange}
          />
        ) : null}
        {blocks.map((block) => (
          <NoticeStrip
            key={block.code}
            tone="error"
            action={
              <Button variant="outline" className="min-h-12" onClick={focusReceiptReasonFields}>
                Указать парное списание
              </Button>
            }
          >
            {block.message}
          </NoticeStrip>
        ))}
        {missing > 0 ? (
          <NoticeStrip tone="warning">
            {missingExpiryTitle(missing)} — партии будут помечены «срок не указан».
            Проведение не блокируется.
          </NoticeStrip>
        ) : null}
      </>
    );
  }

  return (
    <>
      {canPrint && labels && labels.totalItems > 0 ? (
        <ReceiptLabelsBlock status={labels} onPrint={onPrint} onOpenHistory={onOpenHistory} />
      ) : null}
      {labels && labels.staleItems > 0 ? (
        <NoticeStrip tone="warning">
          {staleNotice(labels.staleItems)} {LABEL_TEXT.staleFood}
        </NoticeStrip>
      ) : null}
      {missing > 0 ? (
        <NoticeStrip tone="warning">
          {missingExpiryTitle(missing)}: укажите срок через «⋯» строки — этикетки
          партии после этого нужно перепечатать.
        </NoticeStrip>
      ) : null}
      {unplaced > 0 ? (
        <NoticeStrip tone="info">
          {unplaced} из {lotsTotal} партий приёмки ещё без адреса — разложите их
          по ячейкам одним документом.
        </NoticeStrip>
      ) : null}
    </>
  );
}
